import {
  getKotaNakshatraSequence,
  KOTA_LAYER_BY_NUMBER,
  KOTA_POSITIONS,
  PLANETS,
} from '../constants.js';
import { createDisclaimerElement } from '../disclaimer.js';
import {
  clearElement,
  createTitle,
  enrichPlanetList,
  htmlElement,
  svgElement,
} from '../utils.js';

const WIDTH = 820;
const HEIGHT = 760;

export function renderKotaChakra(container, snapshot) {
  clearElement(container);
  const model = normalizeModel(snapshot);
  const layout = htmlElement('div', { className: 'kota-layout' });
  const chartHost = htmlElement('div', { className: 'kota-chart-host' });
  const explanation = renderKotaExplanation(model, snapshot);
  const svg = buildKotaSvg(model, snapshot);
  chartHost.append(renderKotaRulers(model), svg);
  layout.append(chartHost, explanation);
  container.append(layout);
  return svg;
}

function renderKotaRulers(model) {
  const rulers = htmlElement('div', { className: 'kota-rulers' });
  for (const [label, planetId] of [['Kot Swami', model.kotaSwami], ['Kot Pal', model.kotaPala]]) {
    rulers.append(htmlElement('div', { className: 'metric' }, [
      htmlElement('span', { text: label }),
      htmlElement('strong', { text: PLANETS[planetId]?.name || '—' }),
    ]));
  }
  return rulers;
}

function normalizeModel(snapshot) {
  const source = snapshot?.kota || {};
  const planets = enrichPlanetList(source.planets || snapshot?.transit?.planets || [])
    .filter((planet) => planet.id !== 'ascendant');
  const nakshatras = Array.isArray(source.nakshatras) && source.nakshatras.length === 28
    ? source.nakshatras
    : getKotaNakshatraSequence(source.referenceNakshatra);
  return {
    ...source,
    nakshatras,
    planets,
    gocharScore: Number.isFinite(Number(source.gocharScore)) ? Number(source.gocharScore) : null,
    trend: source.trend || null,
    ruleContributions: Array.isArray(source.ruleContributions) ? source.ruleContributions : [],
  };
}

function buildKotaSvg(model, snapshot) {
  const svg = svgElement('svg', {
    viewBox: `0 0 ${WIDTH} ${HEIGHT}`,
    className: 'aat-chart aat-kota-chakra',
    role: 'img',
    'aria-label': 'Kota Chakra with transit planet placements',
  });

  svg.append(svgElement('rect', {
    x: 10,
    y: 10,
    width: WIDTH - 20,
    height: HEIGHT - 20,
    rx: 16,
    className: 'kota-background',
  }));

  drawFortLayers(svg);
  drawFortLines(svg);
  drawNakshatras(svg, model);
  drawPlanets(svg, model);
  drawKotaMeta(svg, model, snapshot);
  return svg;
}

function drawFortLayers(svg) {
  const group = svgElement('g', { className: 'kota-layers' });
  group.append(svgElement('rect', { x: 20, y: 20, width: 780, height: 720, className: 'kota-layer kota-bahya' }));
  group.append(svgElement('rect', { x: 105, y: 100, width: 590, height: 560, className: 'kota-layer kota-prakaara' }));
  group.append(svgElement('rect', { x: 205, y: 195, width: 390, height: 370, className: 'kota-layer kota-madhya' }));
  group.append(svgElement('rect', { x: 305, y: 300, width: 190, height: 190, className: 'kota-layer kota-stambha' }));

  const labels = [
    { label: 'Bahya', x: 400, y: 42 },
    { label: 'Prakaara', x: 400, y: 120 },
    { label: 'Madhya', x: 400, y: 215 },
    { label: 'Stambha', x: 400, y: 322 },
  ];
  labels.forEach(({ label, x, y }) => {
    group.append(svgElement('text', {
      x,
      y,
      className: `kota-layer-label kota-${label.toLowerCase()}`,
      'text-anchor': 'middle',
    }, label));
  });

  svg.append(group);
}

function drawFortLines(svg) {
  const group = svgElement('g', { className: 'kota-lines' });
  const lines = [
    [20, 20, 305, 300], [800, 20, 495, 300], [800, 740, 495, 490], [20, 740, 305, 490],
    [105, 100, 305, 300], [695, 100, 495, 300], [695, 660, 495, 490], [105, 660, 305, 490],
    [205, 195, 305, 300], [595, 195, 495, 300], [595, 565, 495, 490], [205, 565, 305, 490],
    [400, 20, 400, 300], [400, 490, 400, 740],
    [20, 395, 305, 395], [495, 395, 800, 395],
  ];
  lines.forEach(([x1, y1, x2, y2]) => group.append(svgElement('line', {
    x1, y1, x2, y2, className: 'kota-structure-line',
  })));
  svg.append(group);
}

function drawNakshatras(svg, model) {
  const group = svgElement('g', { className: 'kota-nakshatras' });
  KOTA_POSITIONS.forEach((position) => {
    const name = model.nakshatras[position.number - 1];
    const layer = KOTA_LAYER_BY_NUMBER[position.number];
    const active = model.referenceNakshatra && normalizeName(model.referenceNakshatra) === normalizeName(name);
    const label = svgElement('g', {
      className: `kota-nakshatra kota-${layer}${active ? ' is-reference' : ''}`,
      transform: `translate(${position.x} ${position.y})`,
      'data-number': position.number,
      'data-nakshatra': name,
    });
    label.append(svgElement('text', {
      x: 0,
      y: 0,
      className: 'kota-nakshatra-label',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, `${position.number}. ${shortName(name)}`));
    label.append(createTitle(`${position.number}. ${name} — ${capitalise(layer)}`));
    group.append(label);
  });
  svg.append(group);
}

function drawPlanets(svg, model) {
  const group = svgElement('g', { className: 'kota-planets' });
  const groups = KOTA_POSITIONS.map(() => []);
  model.planets.forEach((planet) => {
    const name = planet.kotaNakshatra || planet.sbcNakshatra || planet.nakshatra;
    const index = model.nakshatras.findIndex((item) => normalizeName(item) === normalizeName(name));
    if (index >= 0) groups[index].push(planet);
  });

  layoutPlanetBadges(groups).forEach(({ index, planets, x, y, width, height, columns }) => {
    const position = KOTA_POSITIONS[index];
    const targetX = Math.max(x, Math.min(x + width, position.x));
    const targetY = Math.max(y, Math.min(y + height, position.y));
    group.append(svgElement('line', {
      x1: position.x, y1: position.y + (targetY >= position.y ? 10 : -10),
      x2: targetX, y2: targetY, className: 'kota-planet-connector',
    }));
    planets.forEach((planet, offset) => {
      const name = planet.kotaNakshatra || planet.sbcNakshatra || planet.nakshatra;
      const badge = svgElement('g', {
        className: `kota-planet-badge ${planet.className || PLANETS[planet.id]?.className || 'planet-generic'}`,
        transform: `translate(${x + offset % columns * 52} ${y + Math.floor(offset / columns) * 34})`,
        'data-number': index + 1,
        'data-planet': planet.id,
        'aria-label': `${planet.name} in ${name}; ${planet.retrograde ? 'retrograde' : 'direct'}`,
      });
      badge.append(svgElement('rect', {
        x: 0, y: 0, width: 48, height: 30, rx: 6,
        className: 'kota-planet-badge-bg',
      }));
      badge.append(svgElement('text', {
        x: 24, y: 15, className: 'kota-planet-badge-text',
        'text-anchor': 'middle', 'dominant-baseline': 'central',
      }, `${planet.short}${planet.retrograde ? 'R' : ''}`));
      badge.append(createTitle(`${planet.name} in ${name}; ${planet.retrograde ? 'retrograde' : 'direct'}`));
      group.append(badge);
    });
  });
  svg.append(group);
}

function layoutPlanetBadges(groups) {
  const overlaps = (a, b) => a.x < b.x + b.width + 4 && a.x + a.width + 4 > b.x
    && a.y < b.y + b.height + 4 && a.y + a.height + 4 > b.y;
  const occupied = KOTA_POSITIONS.map(({ x, y }) => ({ x: x - 56, y: y - 10, width: 112, height: 20 }));
  [42, 120, 215, 322].forEach((y) => occupied.push({ x: 355, y: y - 18, width: 90, height: 22 }));
  // Reserve the score, trend and date footer as well as the nakshatra names.
  occupied.push({ x: 42, y: 695, width: 280, height: 40 }, { x: 550, y: 715, width: 228, height: 20 });
  return groups.flatMap((planets, index) => {
    if (!planets.length) return [];
    const point = KOTA_POSITIONS[index];
    const columns = Math.min(2, planets.length);
    const width = columns * 52 - 4;
    const height = Math.ceil(planets.length / columns) * 34 - 4;
    const below = { x: point.x - width / 2, y: point.y + 18 };
    const above = { x: point.x - width / 2, y: point.y - 18 - height };
    const candidates = [
      ...(point.y >= 640 || index === 17 ? [above, below] : [below, above]),
      { x: point.x + 62, y: point.y - height / 2 },
      { x: point.x - 62 - width, y: point.y - height / 2 },
      { x: point.x - width / 2, y: point.y - 60 - height },
      { x: point.x - width / 2, y: point.y + 60 },
    ].map(({ x, y }) => ({ x: Math.max(30, Math.min(790 - width, x)), y: Math.max(30, Math.min(738 - height, y)), width, height }));
    const box = candidates.reduce((best, candidate) =>
      occupied.filter((other) => overlaps(candidate, other)).length < occupied.filter((other) => overlaps(best, other)).length ? candidate : best,
    );
    occupied.push(box);
    return [{ ...box, planets, index, columns }];
  });
}

function drawKotaMeta(svg, model, snapshot) {
  const scoreLabel = model.gocharScore === null
    ? 'Gochar score: not configured'
    : `Gochar score: ${model.gocharScore.toFixed(2)}`;
  const trend = model.trend || (model.gocharScore === null ? 'Calculation view' : 'Neutral');
  const time = snapshot?.meta?.displayDateTime || snapshot?.meta?.localDateTime || '';
  const group = svgElement('g', { className: 'kota-meta' });
  group.append(svgElement('text', { x: 42, y: 708, className: 'kota-score' }, scoreLabel));
  group.append(svgElement('text', { x: 42, y: 728, className: 'kota-trend' }, `Trend: ${trend}`));
  if (time) group.append(svgElement('text', { x: 778, y: 728, className: 'kota-time', 'text-anchor': 'end' }, time));
  svg.append(group);
}

function renderKotaExplanation(model, snapshot) {
  const panel = htmlElement('aside', { className: 'kota-explanation' });
  panel.append(htmlElement('h3', { text: 'Kota interpretation' }));

  const summary = htmlElement('div', { className: 'kota-summary' });
  const score = model.gocharScore === null ? 'Not configured' : model.gocharScore.toFixed(2);
  const trend = model.trend || (model.gocharScore === null ? 'Calculation view' : 'Neutral');
  summary.append(
    htmlElement('div', { className: 'metric' }, [htmlElement('span', { text: 'Score' }), htmlElement('strong', { text: score })]),
    htmlElement('div', { className: 'metric' }, [htmlElement('span', { text: 'Trend' }), htmlElement('strong', { text: trend })]),
    htmlElement('div', { className: 'metric' }, [htmlElement('span', { text: 'Reference' }), htmlElement('strong', { text: model.referenceNakshatra || '—' })]),
  );
  panel.append(summary);

  if (model.ruleContributions.length === 0) {
    panel.append(htmlElement('p', {
      className: 'muted',
      text: 'The chart is ready. Add approved Gochar rules to show a score and interpretive trend. No weights are inferred.',
    }));
  } else {
    const table = htmlElement('table', { className: 'aat-data-table compact' });
    const thead = htmlElement('thead');
    thead.append(htmlElement('tr', {}, [
      htmlElement('th', { text: 'Rule' }),
      htmlElement('th', { text: 'Interpretation' }),
      htmlElement('th', { text: 'Points' }),
    ]));
    const tbody = htmlElement('tbody');
    model.ruleContributions.forEach((entry) => {
      tbody.append(htmlElement('tr', {}, [
        htmlElement('td', { text: entry.rule || entry.id || 'Rule' }),
        htmlElement('td', { text: entry.reason || '—' }),
        htmlElement('td', { text: Number(entry.points || 0).toFixed(2) }),
      ]));
    });
    table.append(thead, tbody);
    panel.append(table);
  }

  panel.append(createDisclaimerElement({
    language: snapshot?.meta?.disclaimerLanguage || 'en',
    className: 'aat-disclaimer kota-disclaimer',
  }));
  return panel;
}

function normalizeName(value) {
  const name = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return name === 'dhanishtha' ? 'dhanishta' : name;
}

function shortName(name) {
  return name
    .replace('Purva ', 'P.')
    .replace('Uttara ', 'U.')
    .replace('Bhadrapada', 'Bhad.')
    .replace('Phalguni', 'Phalg.')
    .replace('Ashadha', 'Ashad.')
    .replace('Mrigashira', 'Mrig.')
    .replace('Shatabhisha', 'Satabhi.');
}

function capitalise(value) {
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}
